import {
  ConfigurationValues,
  WindowSpec,
} from '@cutr/constants/windows/windows.ts';
import {
  CONFIGURATION_KEYS,
  ConfigurationKey,
  COVERS_CONFIGURATION_KEYS,
  CUSTOM_REQUESTS_KEYS,
  DECORATIVE_CONFIGURATION_KEYS,
  FITTING_CONFIGURATION_KEYS,
  GEOMETRY_CONFIGURATION_KEYS,
  Label as LabelType,
  STANDARD_CONFIGURATION_KEYS,
} from '@cutr/constants/windows/windows-configurations.ts';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';

import { Label } from '../../primitives/Label';
import styles from './Configurations.module.css';
import { useConfigurations } from './ConfigurationsContext';
import {
  configurationsRegistry,
  customRequestConfigurationRegistry,
} from './primitives';

export const OrderLevelConfigurations = ({
  renderMode,
  title,
}: {
  renderMode: 'readonly' | 'form';
  title?: string;
}) => {
  if (renderMode === 'readonly') {
    return <ConfigurationsReadOnly title={title} />;
  }

  if (renderMode === 'form') {
    return <OrderConfigurationInputs />;
  }

  return null;
};

export const PositionLevelConfigurations = ({
  renderMode,
  title,
}: {
  renderMode: 'readonly' | 'form';
  title?: string;
}) => {
  if (renderMode === 'readonly') {
    return <ConfigurationsReadOnly title={title} />;
  }

  if (renderMode === 'form') {
    return <PositionConfigurationInputs />;
  }

  return null;
};

const OrderConfigurationInputs = () => {
  const { configurations } = useConfigurations();
  const { t } = useTranslation();

  const keysToRender = CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );
  if (keysToRender.length === 0) {
    return null;
  }

  const systemConfigurationKeys = (
    ['system', 'thickness', 'woodType', 'glassType'] as const
  ).filter((key) => keysToRender.includes(key));

  const frameConfigurationKeys = (
    ['paintColorInside', 'paintColorOutside', 'aluCladdingColor'] as const
  ).filter((key) => keysToRender.includes(key));

  const sealantConfigurationKeys = (
    ['sealantInnerColor', 'sealantOuterColor', 'sealOptions'] as const
  ).filter((key) => keysToRender.includes(key));

  const hardwareConfigurationKeys = (['handle', 'hardware'] as const).filter(
    (key) => keysToRender.includes(key)
  );

  const hasColorConfiguration =
    frameConfigurationKeys.length > 0 ||
    hardwareConfigurationKeys.length > 0 ||
    sealantConfigurationKeys.length > 0;

  const geometryConfigurationKeys = (
    ['windowSills', 'windowSillDecorativeProfile'] as const
  ).filter((key) => keysToRender.includes(key));

  const coversConfigurationKeys = (
    [...COVERS_CONFIGURATION_KEYS] as const
  ).filter((key) => keysToRender.includes(key));

  const coverStripsEnabled = keysToRender.includes('coverStrips');
  const plasteringStripsEnabled = keysToRender.includes('plasteringStrips');
  const frameBeamWidthEnabled = keysToRender.includes('frameBeamWidth');
  const sashBeamWidthEnabled = keysToRender.includes('sashBeamWidth');
  const beamWidthEnabled = frameBeamWidthEnabled || sashBeamWidthEnabled;
  const sashbarsEnabled = keysToRender.includes('sashbars');

  const hasAdvancedConfigurations =
    geometryConfigurationKeys.length > 0 ||
    coversConfigurationKeys.length > 0 ||
    coverStripsEnabled ||
    plasteringStripsEnabled ||
    beamWidthEnabled ||
    sashbarsEnabled;

  return (
    <div className={cn(styles.configurations, styles.inputs)}>
      {/* System config */}
      <h3 style={{ marginBottom: 'var(--space-s)' }}>
        {t('reseller.common.configuration')}
      </h3>
      {systemConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Color config */}
      {hasColorConfiguration && (
        <h3>{t('reseller.common.colorConfiguration')}</h3>
      )}

      {/* Frame subheader */}
      {frameConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.frame')} />
      )}
      {frameConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Sealant subheader */}
      {sealantConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.sealant')} />
      )}
      {sealantConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Hardware subheader */}
      {hardwareConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.hardware')} />
      )}
      {hardwareConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      <>
        {/* Advanced config */}
        {hasAdvancedConfigurations && (
          <h3>{t('reseller.common.advancedConfiguration')}</h3>
        )}

        {geometryConfigurationKeys.length > 0 && (
          <Subheader title={t('reseller.orderConfig.windowSills')} />
        )}
        {geometryConfigurationKeys.map((key) => (
          <React.Fragment key={key}>
            {configurationsRegistry[key]?.input}
          </React.Fragment>
        ))}

        {coversConfigurationKeys.length > 0 && (
          <Subheader title={t('reseller.orderConfig.rainProtection')} />
        )}
        {coversConfigurationKeys.map((key) => (
          <React.Fragment key={key}>
            {configurationsRegistry[key]?.input}
          </React.Fragment>
        ))}

        {coverStripsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.coverStrips')} />
            {configurationsRegistry.coverStrips?.input}
          </>
        )}
        {plasteringStripsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.plasteringStrips')} />
            {configurationsRegistry.plasteringStrips?.input}
          </>
        )}
        {beamWidthEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.customBeamWidths')} />
            {frameBeamWidthEnabled &&
              configurationsRegistry.frameBeamWidth?.input}
            {sashBeamWidthEnabled &&
              configurationsRegistry.sashBeamWidth?.input}
          </>
        )}

        {sashbarsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.sashbars')} />
            {configurationsRegistry.sashbars?.input}
          </>
        )}
      </>
    </div>
  );
};

const PositionConfigurationInputs = () => {
  const { configurations } = useConfigurations();
  const { t } = useTranslation();

  const keysToRender = CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const systemConfigurationKeys = (
    ['system', 'thickness', 'woodType', 'glassType'] as const
  ).filter((key) => keysToRender.includes(key));

  const frameConfigurationKeys = (
    ['paintColorInside', 'paintColorOutside', 'aluCladdingColor'] as const
  ).filter((key) => keysToRender.includes(key));

  const sealantConfigurationKeys = (
    ['sealantInnerColor', 'sealantOuterColor', 'sealOptions'] as const
  ).filter((key) => keysToRender.includes(key));

  const hardwareConfigurationKeys = (['handle', 'hardware'] as const).filter(
    (key) => keysToRender.includes(key)
  );

  const windowSillsConfigurationKeys = (
    ['windowSills', 'windowSillDecorativeProfile'] as const
  ).filter((key) => keysToRender.includes(key));

  const positionCoversConfigurationKeys = (
    [...COVERS_CONFIGURATION_KEYS] as const
  ).filter((key) => keysToRender.includes(key));

  const hasWindowSillsConfigurations = windowSillsConfigurationKeys.length > 0;

  const coverStripsEnabled = keysToRender.includes('coverStrips');
  const plasteringStripsEnabled = keysToRender.includes('plasteringStrips');
  const frameBeamWidthEnabled = keysToRender.includes('frameBeamWidth');
  const sashBeamWidthEnabled = keysToRender.includes('sashBeamWidth');
  const beamWidthEnabled = frameBeamWidthEnabled || sashBeamWidthEnabled;
  const sashbarsEnabled = keysToRender.includes('sashbars');

  return (
    <div className={cn(styles.configurations, styles.inputs)}>
      {/* System config */}
      {systemConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.common.configuration')} />
      )}
      {systemConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Frame subheader */}
      {frameConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.frame')} />
      )}
      {frameConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Sealant subheader */}
      {sealantConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.sealant')} />
      )}
      {sealantConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      {/* Hardware subheader */}
      {hardwareConfigurationKeys.length > 0 && (
        <Subheader title={t('reseller.orderConfig.hardware')} />
      )}
      {hardwareConfigurationKeys.map((key) => (
        <React.Fragment key={key}>
          {configurationsRegistry[key].input}
        </React.Fragment>
      ))}

      <>
        {hasWindowSillsConfigurations && (
          <Subheader title={t('reseller.orderConfig.windowSills')} />
        )}
        {windowSillsConfigurationKeys.map((key) => (
          <React.Fragment key={key}>
            {configurationsRegistry[key]?.input}
          </React.Fragment>
        ))}
        {positionCoversConfigurationKeys.length > 0 && (
          <Subheader title={t('reseller.orderConfig.rainProtection')} />
        )}
        {positionCoversConfigurationKeys.map((key) => (
          <React.Fragment key={key}>
            {configurationsRegistry[key]?.input}
          </React.Fragment>
        ))}
        {coverStripsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.coverStrips')} />
            {configurationsRegistry.coverStrips?.input}
          </>
        )}
        {plasteringStripsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.plasteringStrips')} />
            {configurationsRegistry.plasteringStrips?.input}
          </>
        )}
        {beamWidthEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.customBeamWidths')} />
            {frameBeamWidthEnabled &&
              configurationsRegistry.frameBeamWidth?.input}
            {sashBeamWidthEnabled &&
              configurationsRegistry.sashBeamWidth?.input}
          </>
        )}
        {sashbarsEnabled && (
          <>
            <Subheader title={t('reseller.orderConfig.sashbars')} />
            {configurationsRegistry.sashbars?.input}
          </>
        )}
      </>

      <>
        {/* Custom requests subheader */}
        <Subheader title={t('reseller.orderConfig.customRequests')} />
        {customRequestConfigurationRegistry.customRequest.input}
      </>
    </div>
  );
};

export const Subheader = ({ title }: { title: string }) => {
  return (
    <div className={styles.subheader}>
      <div>
        <strong>{title}</strong>
      </div>
    </div>
  );
};

export const SubSubheader = ({ title }: { title: string }) => {
  return (
    <div className={cn(styles.subheader, styles.subsubheader)}>
      <div>
        <strong>{title}</strong>
      </div>
    </div>
  );
};

const ConfigurationsReadOnly = ({ title }: { title?: string }) => {
  const { configurations } = useConfigurations();
  const { t } = useTranslation();

  const standardKeysToRender = STANDARD_CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const geometryKeysToRender = GEOMETRY_CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const coversKeysToRender = COVERS_CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const fittingKeysToRender = FITTING_CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const decorativeKeysToRender = DECORATIVE_CONFIGURATION_KEYS.filter(
    (key) => key in configurations
  );

  const customRequestKeysToRender = CUSTOM_REQUESTS_KEYS.filter(
    (key) =>
      configurations?.customRequests && key in configurations.customRequests
  );

  if (
    standardKeysToRender.length === 0 &&
    geometryKeysToRender.length === 0 &&
    coversKeysToRender.length === 0 &&
    fittingKeysToRender.length === 0 &&
    decorativeKeysToRender.length === 0 &&
    customRequestKeysToRender.length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.configurations}>
      <h3>{title || t('reseller.common.configuration')}</h3>
      {STANDARD_CONFIGURATION_KEYS.map((key) => {
        const value = configurations[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>
              <ConfigurationDisplay
                key={key}
                configurationKey={key}
                value={value}
              />
            </div>
          </React.Fragment>
        );
      }).filter(Boolean)}
      {GEOMETRY_CONFIGURATION_KEYS.map((key) => {
        const value = configurations[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>
              <ConfigurationDisplay
                key={key}
                configurationKey={key}
                value={value}
              />
            </div>
          </React.Fragment>
        );
      }).filter(Boolean)}
      {COVERS_CONFIGURATION_KEYS.map((key) => {
        const value = configurations[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>
              <ConfigurationDisplay
                key={key}
                configurationKey={key}
                value={value}
              />
            </div>
          </React.Fragment>
        );
      }).filter(Boolean)}
      {FITTING_CONFIGURATION_KEYS.map((key) => {
        const value = configurations[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>
              <ConfigurationDisplay
                key={key}
                configurationKey={key}
                value={value}
              />
            </div>
          </React.Fragment>
        );
      }).filter(Boolean)}
      {DECORATIVE_CONFIGURATION_KEYS.map((key) => {
        const value = configurations[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>
              <ConfigurationDisplay
                key={key}
                configurationKey={key}
                value={value}
              />
            </div>
          </React.Fragment>
        );
      }).filter(Boolean)}
      {CUSTOM_REQUESTS_KEYS.map((key) => {
        const value = configurations?.customRequests?.[key];
        if (!value) return null;
        return (
          <React.Fragment key={key}>
            <Label>{t(`windows.configurations.${key}.label`)}</Label>
            <div>{customRequestConfigurationRegistry[key]?.display(value)}</div>
          </React.Fragment>
        );
      }).filter(Boolean)}
    </div>
  );
};

export const AreaLevelConfigurations = ({
  areaConfigurations,
}: {
  areaConfigurations: WindowSpec['areaConfigurations'];
}) => {
  const { t } = useTranslation();

  const overrides = Object.entries(areaConfigurations).filter(
    ([, configuration]) => configuration?.sashbars
  );

  if (overrides.length === 0) {
    return null;
  }

  return (
    <div className={styles.configurations}>
      <h3>{t('reseller.common.areaConfigurationOverrides')}</h3>
      {overrides.map(([areaId, configuration]) => (
        <React.Fragment key={areaId}>
          <Label>
            {t('reseller.areaConfig.areaLabel', {
              number: areaId.replace('area', ''),
            })}
          </Label>
          <div>
            <ConfigurationDisplay
              configurationKey="sashbars"
              value={configuration?.sashbars}
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export const ConfigurationDisplay = <K extends ConfigurationKey>({
  configurationKey,
  value,
}: {
  configurationKey: K;
  value: ConfigurationValues[K];
}) => {
  return configurationsRegistry[configurationKey]?.display(value);
};

export const unboxLabel = (label?: LabelType) => {
  if (!label) {
    return '-';
  }

  const resolvedLanguage = i18n.resolvedLanguage;

  if (resolvedLanguage && resolvedLanguage in label) {
    return label[resolvedLanguage as keyof typeof label];
  }

  return label.en;
};
